import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ImageIcon, Maximize2 } from 'lucide-react';

const Gallery = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [selectedImg, setSelectedImg] = useState(null);

  const images = [
    { id: 1, ext: 'jpg' },
    { id: 2, ext: 'avif' },
    { id: 3, ext: 'jpeg' },
    { id: 4, ext: 'webp' },
    { id: 5, ext: 'jpeg' },
    { id: 6, ext: 'webp' },
    { id: 7, ext: 'jpeg' },
    { id: 8, ext: 'webp' },
    { id: 9, ext: 'jpeg' },
    { id: 10, ext: 'jpg' },
    { id: 11, ext: 'jpg' },
    { id: 12, ext: 'webp' },
    { id: 13, ext: 'webp' },
    { id: 14, ext: 'jpeg' },
    { id: 15, ext: 'webp' },
    { id: 16, ext: 'webp' },
    { id: 17, ext: 'jpg' },
    { id: 18, ext: 'webp' },
    { id: 19, ext: 'webp' },
    { id: 20, ext: 'webp' },
  ].map((img) => ({
    id: img.id,
    // Note the added / after gallery to indicate a folder
    url: `/images/gallery/${img.id}.${img.ext}`,
    titleEn: `Healthcare Excellence ${img.id}`,
    titleAr: `تميز الرعاية الصحية ${img.id}`,
  }));

  return (
    <div
      className='pt-24 pb-20 bg-[#050505] min-h-screen'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* Header Section */}
      <section className='section-padding py-16 text-center'>
        <div className='max-w-7xl mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className='inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-medical-blue-400 text-xs font-black uppercase tracking-widest mb-6'>
              <ImageIcon size={16} />
              {isAr ? 'معرض الصور' : 'Visual Gallery'}
            </div>
            <h1 className='text-4xl md:text-6xl font-black text-white mb-6'>
              {isAr ? 'لحظات من الرعاية' : 'Moments of Care'}
            </h1>
            <p className='text-lg text-gray-400 max-w-2xl mx-auto font-medium'>
              {isAr
                ? 'نظرة داخلية على خدماتنا، طاقمنا الطبي، والتزامنا بتقديم أعلى معايير الرعاية الصحية في الكويت.'
                : 'An inside look at our services, medical staff, and commitment to providing the highest standards of healthcare in Kuwait.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Image Grid Section */}
      <section className='section-padding max-w-7xl mx-auto'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
          {images.map((img, index) => (
            <motion.div
              key={img.id}
              layoutId={`img-${img.id}`}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setSelectedImg(img)}
              className='group relative aspect-square rounded-[2rem] overflow-hidden bg-white/5 border border-white/10 cursor-pointer'
            >
              <img
                src={img.url}
                alt={isAr ? img.titleAr : img.titleEn}
                className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
              />
              {/* Hover Overlay */}
              <div className='absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm'>
                <div className='p-4 bg-medical-blue-600 rounded-full text-white transform translate-y-4 group-hover:translate-y-0 transition-transform'>
                  <Maximize2 size={24} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl'
            onClick={() => setSelectedImg(null)}
          >
            <button
              className='absolute top-8 right-8 p-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors'
              onClick={() => setSelectedImg(null)}
            >
              <X size={32} />
            </button>

            <motion.div
              layoutId={`img-${selectedImg.id}`}
              className='max-w-5xl w-full h-auto max-h-[80vh] rounded-[3rem] overflow-hidden border-4 border-white/10 relative'
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImg.url}
                alt='Expanded view'
                className='w-full h-full object-contain bg-black'
              />
              <div className='absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black to-transparent text-center'>
                <h3 className='text-white text-xl font-black'>
                  {isAr ? selectedImg.titleAr : selectedImg.titleEn}
                </h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
